import { EditNames, EditTarget, SegmentationControllerType } from "config/analysisConfig";
import { BrushPlane, InitSegState, StateApplier, StrokeSession, Vec3 } from "./SegmentManagerTypes";
import { applyExternalVoxelEditState, clearSegState, commitExternalVoxelEditHistory, createSegmentManagerDraftSnapshot, endStrokeState, ensureActiveMask, initializeSegState, packManipSegState, packRegulatorState, paintAtPointIndices, paintAtPointStreaming, paintAtPointStreamingWithinIndices, redoSegState, registerStateApplier, requestRenderUpdate, resetSegmentMasks, restoreSegmentManagerDraftSnapshot, undoSegState } from "./SegmentManagerCore";
import type { ExternalVoxelEditParams } from "./SegmentManagerCore";
import { SurfaceContext } from "@features/analysis/rendering/Panels/Renderers/vtkManagers";
import { loadConfig } from "@api/api";
import axios, { AxiosResponse } from "axios";
import { configStore } from "config/configStore";
import { getManualVolumeFromSeriesId } from "@features/analysis/rendering/Panels/Renderers/CornerstoneManager";
import { getInitialPoints } from "@features/view/initialPointSphereMarkers";
import { getWorldToIndex } from "@features/view/Surface3DUtilities";

const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--background-primary-filled').trim();

type BrushSvgOptions = {
  radiusMM: number;
  pixelsPerMM: number;
  brushStep?: number;
  brushUnit?: number;
  range: [number,number];
  modifierType: ModifierType;
  stroke?: string;
  fill?: string;
  opacity?: number;
  strokeOpacity?:number;
  id?: string;
};

export enum ModifierType {
  IDLE = -1,
  ERASER = 0,
  BRUSH = 1,
  PREVIEW = 2,
  POINT=3,
  CONNECT_2D = 4,
};

let sess : StrokeSession | null = null;
let context : SurfaceContext | undefined;

export function initializeSegmentManager(params : InitSegState, cntx:SurfaceContext){
  initializeSegState(params);
  context=cntx;
  // if(params.segmentLabel){
  //   ensureActiveMask(params.segmentLabel,context);
  // }
}

export function setStateApplier(applier:StateApplier){
  registerStateApplier(applier);
}
export function exitSegmentManager(){
  if(!context)return;
  ensureActiveMask(null,context);
}
export function changeSegmentLabel(segmentLabel:SegmentationControllerType){
  if(!context)return;
  ensureActiveMask(segmentLabel,context);
}

export function makeBrushGroupSvg({
  radiusMM,
  pixelsPerMM,
  brushStep,
  brushUnit,
  range,
  modifierType,
  stroke = primaryColor,
  fill = primaryColor,
  opacity = 0.2,
  strokeOpacity = 0.0,
  id = 'brush',
}: BrushSvgOptions): SVGGElement {
  const r = radiusMM * pixelsPerMM;
  const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  g.setAttribute('id', id);
  g.setAttribute('pointer-events', 'none');

  const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  circle.setAttribute('id', id);
  circle.setAttribute('pointer-events', 'none');
  circle.setAttribute('cx', '0');
  circle.setAttribute('cy', '0');
  circle.setAttribute('r', r.toFixed(2));
  circle.setAttribute('stroke', stroke);
  circle.setAttribute('stroke-opacity', String(strokeOpacity));
  circle.setAttribute('fill', fill);
  circle.setAttribute('fill-opacity', String(opacity));
  circle.setAttribute('stroke-width', '1');
  circle.setAttribute('radiusMM',String(radiusMM));
  circle.setAttribute('pixelsPerMM',String(pixelsPerMM));
  if (brushStep !== undefined) circle.setAttribute('brushStep', String(brushStep));
  if (brushUnit !== undefined) circle.setAttribute('brushUnit', String(brushUnit));
  circle.setAttribute('range',String(range));
  circle.setAttribute('modifierType',String(modifierType));

  g.appendChild(circle);
  return g;
}


export function beginStroke(
  params: {
    // segmentLabel: SegmentationControllerType;
    imageData: any;
    // labelScalar: Uint8Array;
    plane: BrushPlane;
    radiusMM: number;
    pixelsPerMM: number;
    segmentIndex:number;
    flippedThisStroke?:number[];
    flood?:boolean
    disableClosedFill?: boolean;
    // setAtIJKPointLabelScalar: (index: any, v: any) => boolean;
    // requestRender: () => void;
    // labelmapVolumeId: string; // 이미 생성한 labelmap volume ID
  }
) {
  // ensureActiveMask(params.segmentLabel);

  const dim =  params.imageData.getDimensions()
  const origin = params.imageData.getOrigin();     // [ox,oy,oz]
  const spacing =  params.imageData.getSpacing();   // [sx,sy,sz]
  const direction =  params.imageData.getDirection(); // row-major 3x3
  sess = {
    // segmentLabel: params.segmentLabel,
    segmentIndex: params.segmentIndex,
    // imageData: params.imageData,
    vol:{dim,origin,spacing,direction},
    // labelScalar:params.labelScalar,
    plane: params.plane,
    radiusMM: params.radiusMM,
    pixelsPerMM:params.pixelsPerMM,
    lastWorld: params.flood ? undefined : params.plane.centerWorld,
    pointsWorld: params.flood ? [] : [params.plane.centerWorld],
    disableClosedFill: params.disableClosedFill,
    flippedThisStroke: paintAtPointIndices(params.flippedThisStroke,params.flood)??[],
    bbox: { i0: dim[0]-1, j0: dim[1]-1, k0: dim[2]-1, i1:0, j1:0, k1:0 },
    dirtyKeys: new Set<number>(),
  };

  // 이거는 flippedThisStroke가 들어왔을 경우에 한빵에 처리하려고.
  // preview만 해당하고 begin->fill->endStroke사이를 최소화 하려고
  ;

  // requestRenderUpdate(context!);
}

export function endStroke(){
  if(!sess || !context) return;
  endStrokeState(sess,context);
  sess = null;
}

export function undoStroke(){
  if(!context) return;
  undoSegState(context);
}
export function redoStroke(){
  if(!context) return;
  redoSegState(context);
}
export function paintAtPoint(world: Vec3){
  if(!sess || !context) return;

  paintAtPointStreaming(sess,context,world);

  // requestAnimationFrame(sess.requestRender);

    // if(!!sess.dirtyKeys.size){
  // requestAnimationFrame(globalState.updateElements);
  // request3DUpdate(cntx);
    // }
  requestRenderUpdate(context,true);
}

export function paintAtPointWithinIndices(
  world: Vec3,
  allowedIndices: Uint32Array | null | undefined,
) {
  if(!sess || !context) return;

  const paintedCount = paintAtPointStreamingWithinIndices(
    sess,
    context,
    world,
    allowedIndices,
  );
  if (paintedCount > 0) {
    requestRenderUpdate(context,true);
  }
}

export function applyVoxelIJKEditToSegmentManager(
  params: ExternalVoxelEditParams,
) {
  if(!context) return null;
  return applyExternalVoxelEditState(params, context);
}

export function commitVoxelIJKEditToSegmentManagerHistory(sourceId: string) {
  if(!context) return null;
  return commitExternalVoxelEditHistory(sourceId);
}

// export function onBrushApply(stroke: DragStroke){
//   applyBrushStrokeFill
// }

export function clearSegmentManager(){
  clearSegState(context);
  context = undefined;
  sess=null;
}
export function hasSess(){return sess;};
export function discardSegmentManager(setDirty:any){
  if(!context)return;
  if(sess){
    endStrokeState(sess,context);
    sess = null;
  }
  resetSegmentMasks(context);
  setDirty(false)
}


export function createSegmentManagerDraft(){
  if(sess && context){
    endStrokeState(sess, context);
    sess = null;
  }
  return createSegmentManagerDraftSnapshot();
}

export function restoreSegmentManagerDraft(draft: ReturnType<typeof createSegmentManagerDraftSnapshot>){
  if(!context || !draft) return false;
  return restoreSegmentManagerDraftSnapshot(draft, context);
}

type PackedBitInts = bigint[] | BigInt64Array;

function bigIntArrayToBlob(values: PackedBitInts): Blob {
  const buffer =
    values instanceof BigInt64Array
      ? (() => {
          const nextBuffer = new ArrayBuffer(values.byteLength);
          new Uint8Array(nextBuffer).set(
            new Uint8Array(
              values.buffer,
              values.byteOffset,
              values.byteLength,
            ),
          );
          return nextBuffer;
        })()
      : (() => {
          const nextBuffer = new ArrayBuffer(values.length * 8);
          const view = new DataView(nextBuffer);
          values.forEach((v, i) => {
            view.setBigInt64(i * 8, v, true);
          });
          return nextBuffer;
        })();

  return new Blob([buffer], {
    type: "application/octet-stream",
  });
}

async function buildModificationFormData(
  resultBitInts: PackedBitInts,
  COMPONENT: number[],
) {
  const formData = new FormData();

  const editMode = configStore.get().EditMode;

  if (editMode == null) return;

  const modeMap: Partial<Record<Exclude<EditTarget, null>, number>> = {
    [EditNames.lumen]: 0,
    [EditNames.regulator]: 1,
  };

  const mode = modeMap[editMode] ?? 0;
  formData.append(
    'data',
    JSON.stringify({
      MODIFY_INFO: {
        MODE: mode,
        COMPONENT,
      },
    }),
  );

  const blob = bigIntArrayToBlob(resultBitInts);
  console.log(resultBitInts.length, COMPONENT);
  const file = new File([blob], 'modify.bin', {
    type: 'application/octet-stream',
  });

  formData.append('file', file);
  return formData;
}

export async function applySegmentManager(
  accessToken:string,
  seriesId:string,
  isRegulator: boolean,
): Promise<boolean> {
  console.time('시시작')
  const segState = isRegulator
    ? await packRegulatorState(seriesId)
    : await packManipSegState();

  console.timeEnd('시시작')
  console.log(segState, isRegulator);
 // console.log(await packManipSegState())
  if (!segState) return false;

  const formData = await buildModificationFormData(
    segState.resultBitInts,
    segState.changedLabels,
  );

  const BASE_URL = await loadConfig();
  const url = `${BASE_URL}/api/v1/series/modification/${seriesId}`;

  const res: AxiosResponse = await axios({
    method: 'post',
    url: url,
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
    },
    data: formData,
  });

  // window.electron.manual.setDirty(false);
  return res.status === 200 || res.status === 201;
}

// type Action={
//   cb: any;
//   label: string;
//   color?: string
// }
export function isInitialPointInSegmentation(seriesId: string) {
  if (!context) {
    return;
  }
  const worlds = getInitialPoints(context);
  const volume = getManualVolumeFromSeriesId(seriesId);
  if (!volume) {
    console.log(`Manual Volume not found`);
    return;
  }
  const { imageData, voxelManager, origin, spacing, dimensions } = volume;
  if (!imageData || !voxelManager) {
    console.log(`ImageData or voxelManager not found`);
    return;
  }
  const scalarData = voxelManager.getCompleteScalarDataArray?.();

  if (!scalarData) {
    console.log(`Scalar data not found`);
    return;
  }
  const half = configStore.get().generalSetting.renderingQuality !== "high";
  const indices: any[] = [];
  for (const world of worlds) {
    const seedIJK = getWorldToIndex(world, spacing, origin, half);
    const i = (Math.round(seedIJK[0]));
    const j = Math.round(seedIJK[1]);
    const k = Math.round(seedIJK[2]);
    const index = i + j * dimensions[0] + k * dimensions[0] * dimensions[1];
    const value = scalarData[index];
    if (value === 0) {
      indices.push({ index, seedIJK});
    }
  }
  console.log(indices, indices.length !== 0);
  return indices.length !== 0;
}
